<script lang="ts">
  import AdminSidebar from "app/components/space/sidebar/AdminSidebar.svelte";
  import CommunityColors from "$components/+headless/CommunityColors.svelte";
  import GlobalTopBar from "$components/+layout/TopBar/GlobalTopBar.svelte";
  import CreateUpdateThreadModal from "$components/forum/CreateUpdateThreadModal/CreateUpdateThreadModal.svelte";
  import OnboardingModal from "$components/user/OnboardingModal/OnboardingModal.svelte";
  import Search from "$components/ai/search/Search.svelte";
  import { page } from "$app/stores";
  import { community, knowledgeBaseUIFlag, spaces } from "$stores/community";
  import { thread } from "$stores/thread";
  import { forum } from "$stores/forum";
  import { canUserViewCommunity } from "$stores/permissions";
  import { goto } from "$app/navigation";
  import { overrideItemIdKeyNameBeforeInitialisingDndZones } from "svelte-dnd-action";
  import { sidePanel } from "$stores/sidePanel";
  import { userHasOutverseEmail } from "$stores/auth";
  import { publicRoutes, threadURL } from "$utils/urls";
  import { browser } from "$app/environment";
  import { DocumentsStore } from "$stores/documents";
  import { documentAPI } from "$stores/documents_api";
  import { setContext } from "svelte";
  import { slugify } from "app/utils/strings";
  import { getStores } from "$stores";
  import { search } from "app/stores/search";
  import { showAdminPanel } from "app/stores/adminPanel";
  import cx from "classnames";
  import Toolbar from "app/components/dev-toolbar/Toolbar.svelte";
  import { devToolbar } from "app/stores/devToolbar";
  import SkipLink from "app/components/+shared/SkipLink/SkipLink.svelte";
  import { navigateToErrorPage } from "app/utils/errors.js";

  // override default dnd library key name
  overrideItemIdKeyNameBeforeInitialisingDndZones("uuid");

  export let data;
  const { user } = getStores();

  $: communitySlug = data.communitySlug;

  const docs = new DocumentsStore(documentAPI);
  setContext("docs", docs);
  $: docs.init(communitySlug);

  // clears/hydrates community data when switching communities
  $: {
    community.hydrate(data.community, data.permissions);
    sidePanel.clearPanel();
    sidePanel.hide();
    forum.init(communitySlug);
    spaces.init(communitySlug);
  }

  $: if (browser) {
    // load on client side/can't be pre-loaded in SSR/load
    community.fetch(communitySlug);
    // docs.init(communitySlug);
  }

  // redirect to join page when not in space & user isn't a member of a private community
  $: isPublicRoute = publicRoutes.some((routeId) => $page.route?.id?.includes(routeId));
  $: canUserViewPage = isPublicRoute || $canUserViewCommunity;
  $: isObservatoryiFrameRoute = $page.route.id?.includes("/observatory/device-sandbox"); // observatory iFrame route for internal use
  $: isObservatoryRoute = $page.route.id?.includes("/observatory"); // observatory route for internal use
  $: userCanViewToolbar = !__IS_PROD__ && $userHasOutverseEmail && !isObservatoryRoute;

  $: if (browser && !canUserViewPage) {
    navigateToErrorPage(404);
  }

  // hide global top bar on specific pages or if 404 and user is not authed
  $: showTopbar = !(
    $page.route.id?.startsWith("/[community=slug]/conversation-history") ||
    $page.route.id?.startsWith("/[community=slug]/settings") ||
    $page.route.id?.startsWith("/[community=slug]/insights") ||
    $page.route.id?.startsWith("/[community=slug]/demo") ||
    $page.route.id?.startsWith("/[community=slug]/context-sources") ||
    $page.route.id?.startsWith("/[community=slug]/beta-sandbox") ||
    $page.route.id?.startsWith("/[community=slug]/intents") ||
    $page.route.id?.startsWith("/[community=slug]/agents") ||
    $page.route.id?.startsWith("/[community=slug]/observatory") ||
    ($page.error?.message.includes("404") && !$user.isAuthed)
  );
</script>

<!-- Add noindex meta for all pages in non-indexable communities -->
<svelte:head>
  {#if !data.community.indexable}
    <!-- We use this instead of a robots.txt file because the robots.txt file is used to prevent crawling but pages can still appear if they are linked to from other sites. -->
    <meta name="robots" content="noindex" />
  {/if}
</svelte:head>

{#if canUserViewPage}
  <SkipLink />
  <CommunityColors />

  {#if $showAdminPanel && !isObservatoryiFrameRoute}
    <span class="hidden bg-neutral-100 md:block dark:bg-neutral-950">
      <AdminSidebar {communitySlug} />
    </span>
  {/if}
  <div
    class={cx("h-dvh min-w-0 grow bg-neutral-100 dark:bg-neutral-950", {
      "md:py-2 md:pr-2": $showAdminPanel && !isObservatoryiFrameRoute,
    })}
  >
    <div
      class="flex h-full flex-col overflow-hidden rounded-md border border-black/5 bg-white dark:border-white/5"
      class:dark:bg-dark-custom-background={$community.backgroundColorDark}
      class:dark:bg-neutral-900={!$community.backgroundColorDark}
    >
      {#if showTopbar}
        <GlobalTopBar />
      {/if}

      <slot />
    </div>
    {#if $devToolbar && userCanViewToolbar}
      <Toolbar />
    {/if}
  </div>
  {#if $user.isAuthed}
    <CreateUpdateThreadModal
      user={$user}
      bind:open={$thread.threadModal.open}
      thread={$thread.threadModal.thread}
      on:updateThread={(event) => {
        const updatedThread = event.detail;
        forum.updateThread(updatedThread);
        if (updatedThread.uuid === $thread.activeThread?.forumThread.uuid) {
          thread.updateActiveThread(updatedThread);
        }
        thread.closeThreadModal();
      }}
      on:createThread={({ detail: newThread }) => {
        $thread.threadModal.open = false;
        goto(
          threadURL({
            communitySlug,
            spaceUuid: newThread.forumSpace.uuid,
            spaceSlug: slugify(newThread.forumSpace.title),
            threadUuid: newThread.uuid,
            threadSlug: slugify(newThread.title),
          })
        );
      }}
    />
  {/if}
{/if}

<!-- search modal -->
<Search />
<OnboardingModal />

<svelte:window
  on:keydown={(e) => {
    // close modal on Escape
    if (e.key === "Escape" && $thread.threadModal.open) {
      thread.closeThreadModal();
      e.preventDefault();
    }
    // listen for the key combination "Ctrl/Cmd + K" to trigger the search modal opening
    if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
      if ($knowledgeBaseUIFlag) {
        e.preventDefault();
        $search = true;
      }
    }
  }}
/>
